<template>
  <div class="tool-selector">
    <header class="header">
      <h1>EditingPrep</h1>
      <p class="subtitle">Choose your tool for professional video editing workflow</p>
      <div class="header-actions">
        <button @click="goToDocumentation" class="docs-button">
          📚 View Documentation
        </button>
      </div>
    </header>

    <main class="tools-container">
      <div class="tool-card" @click="selectTool('ixmlrenamer')">
        <div class="tool-image">
          <img 
            src="/ixmlrenamer/manual/images/ixml-proj.png" 
            alt="iXML Renamer Tool"
            class="thumbnail"
          />
        </div>
        <div class="tool-content">
          <h2>iXML Renamer</h2>
          <p class="tool-description">
            Simple and efficient tool for adding track names to audio clips and renaming audio files based on iXML metadata.
          </p>
        </div>
      </div>

      <div class="tool-card" @click="selectTool('preptoolkit')">
        <div class="tool-image">
          <img 
            src="/docs/preptoolkit/img/metadata-multicam.jpg" 
            alt="PrepToolkit Tool"
            class="thumbnail"
          />
        </div>
        <div class="tool-content">
          <h2>PrepToolkit</h2>
          <p class="tool-description">
            Advanced toolkit for professional video editing workflows. Includes everything in iXML Renamer plus additional features such as auto sequence, timecode manipulation, sync correction, and helper tools for copying metadata.
          </p>
        </div>
      </div>
    </main>

    <footer class="footer">
      <p>Professional tools for video editing workflow optimization</p>
    </footer>
  </div>
</template>

<script setup>
const selectTool = (toolName) => {
  if (toolName === 'ixmlrenamer') {
    window.location.href = '/ixmlrenamer/';
  } else if (toolName === 'preptoolkit') {
    window.location.href = '/preptoolkit/';
  }
};

const goToDocumentation = () => {
  window.location.href = '/docs/';
};
</script>

<style scoped>
.tool-selector {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 3rem;
}

.header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #f7fafc;
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: 1.1rem;
  color: #cbd5e0;
  margin: 0 0 1.5rem 0;
}

.header-actions {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.docs-button {
  background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.docs-button:hover {
  background: linear-gradient(135deg, #3182ce, #2c5282);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.tools-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.tool-card {
  background: #2d3748;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid #4a5568;
}

.tool-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3);
  border-color: #63b3ed;
}

.tool-image {
  height: 250px;
  overflow: hidden;
  background: #1a202c;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.thumbnail {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tool-card:hover .thumbnail {
  transform: scale(1.05);
}

.tool-content {
  padding: 1.5rem;
}

.tool-content h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #f7fafc;
  margin-bottom: 0.75rem;
}

.tool-description {
  color: #cbd5e0;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}

.tool-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.feature-tag {
  background: #4a5568;
  color: #e2e8f0;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tool-card:hover .feature-tag {
  background: #2b6cb0;
  color: #bee3f8;
}

.footer {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid #4a5568;
}

.footer p {
  color: #cbd5e0;
  font-size: 0.9rem;
  margin: 0;
}

/* Responsive design */
@media (max-width: 768px) {
  .tool-selector {
    padding: 1rem;
  }
  
  .tools-container {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .header h1 {
    font-size: 2rem;
  }
  
  .tool-image {
    height: 200px;
    padding: 0.75rem;
  }
}

@media (max-width: 480px) {
  .header h1 {
    font-size: 1.75rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .tool-content {
    padding: 1rem;
  }
  
  .tool-image {
    height: 180px;
    padding: 0.5rem;
  }
}
</style>
